import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  LogIn,
  Eye,
  EyeOff,
  UserPlus,
  CheckCircle,

  ArrowLeft,
  Store,
  Sparkles,
  // Shield,
  Users,
  Lock,
  Key,
  CheckCircle2,
  Clock,
  Zap,
} from "lucide-react";
import { getRedirectPathByRole } from "@/lib/authUtils";

/**
 * Página de inicio de sesión
 *
 * Permite a los usuarios autenticarse en el sistema
 * y los redirige según sus roles.
 * Optimizada para dispositivos móviles con controles táctiles.
 */
const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error, clearError, isAuthenticated, user } =
    useAuth();

  const [rut, setRut] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Obtener mensaje de éxito y email del estado de navegación
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Limpiar el estado para evitar que se muestre en futuras navegaciones
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Redirigir si ya está autenticado
  useEffect(() => {
    if (isAuthenticated && user) {
      const redirectPath = getRedirectPathByRole(user.roles);
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  /**
   * Maneja el envío del formulario de login
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLoading) return;

    const success = await login(rut, password);

    if (success && user) {
      // La redirección se maneja en el useEffect
    }
  };

  /**
   * Limpia el error cuando el usuario empieza a escribir
   */
  const handleInputChange = (field: "rut" | "password", value: string) => {
    if (error) {
      clearError();
    }

    if (field === "rut") {
      setRut(value);
    } else {
      setPassword(value);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Contenido principal - Layout de 2 columnas */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            
            {/* Columna Izquierda - Formulario */}
            <div className="flex flex-col justify-center">
              {/* Hero Section */}
              <div className="text-center lg:text-left mb-4">
                <Badge className="bg-primary/20 text-primary border-primary/30 px-2 py-1 rounded-full mb-2 text-xs">
                  <Sparkles className="h-2.5 w-2.5 mr-1" />
                  Sistema de Gestión Escolar
                </Badge>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground mb-2">
                  Bienvenido de Vuelta
                </h1>
                <p className="text-sm text-muted-foreground">
                  Accede a tu cuenta para gestionar productos y servicios
                  escolares
                </p>
              </div>

              {/* Card de Login */}
              <Card className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-xl">
                <CardHeader className="space-y-1 pb-3">
                  <div className="flex items-center justify-center lg:justify-start mb-2">
                    <div className="p-1.5 bg-gradient-to-br from-primary/20 to-primary/10 rounded-lg border border-primary/20">
                      <LogIn className="h-5 w-5 text-primary" />
                    </div>
                  </div>
                  <CardTitle className="text-lg font-bold text-center lg:text-left text-foreground">
                    Iniciar Sesión
                  </CardTitle>
                  <CardDescription className="text-center lg:text-left text-muted-foreground text-xs">
                    Ingresa tus credenciales para acceder al sistema
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-3">
                  {/* Mensaje de Éxito */}
                  {successMessage && (
                    <Alert className="border-green-200 bg-green-50/50">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <AlertDescription className="text-green-800">
                        {successMessage}
                      </AlertDescription>
                    </Alert>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-3">
                    {/* Campo RUT */}
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="rut"
                        className="text-sm font-medium text-foreground"
                      >
                        RUT
                      </Label>
                      <Input
                        id="rut"
                        type="text"
                        placeholder="Ej: 12.345.678-9"
                        value={rut}
                        onChange={(e) =>
                          handleInputChange("rut", e.target.value)
                        }
                        disabled={isLoading}
                        required
                        className="h-9 text-sm rounded-md border-2 focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                        autoComplete="username"
                      />
                    </div>

                    {/* Campo Contraseña */}
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="password"
                        className="text-sm font-medium text-foreground"
                      >
                        Contraseña
                      </Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Ingresa tu contraseña"
                          value={password}
                          onChange={(e) =>
                            handleInputChange("password", e.target.value)
                          }
                          disabled={isLoading}
                          required
                          className="h-9 text-sm pr-9 rounded-md border-2 focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                          autoComplete="current-password"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-1.5 py-1 hover:bg-transparent focus:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={isLoading}
                          aria-label={
                            showPassword
                              ? "Ocultar contraseña"
                              : "Mostrar contraseña"
                          }
                        >
                          {showPassword ? (
                            <EyeOff className="h-3 w-3 text-muted-foreground" />
                          ) : (
                            <Eye className="h-3 w-3 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Mensaje de Error */}
                    {error && (
                      <Alert
                        variant="destructive"
                        className="border-destructive/50"
                      >
                        <AlertDescription className="text-destructive-foreground">
                          {error}
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* Botón de Login */}
                    <Button
                      type="submit"
                      className="w-full h-9 text-xs font-medium rounded-md bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-200 hover:scale-105 hover:shadow-lg"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                          Iniciando sesión...
                        </>
                      ) : (
                        <>
                          <LogIn className="mr-2 h-3 w-3" />
                          Iniciar Sesión
                        </>
                      )}
                    </Button>
                  </form>

                  {/* Enlaces de Ayuda */}
                  <div className="text-center lg:text-left pt-1 space-y-1">
                    <p className="text-xs text-muted-foreground">
                      ¿No tienes una cuenta?{" "}
                      <Link
                        to="/register"
                        className="inline-flex items-center gap-1 text-primary hover:text-primary/80 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 rounded"
                      >
                        <UserPlus className="h-3 w-3" />
                        Regístrate aquí
                      </Link>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      <Link
                        to="/forgot-password"
                        className="inline-flex items-center gap-1 text-primary hover:text-primary/80 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 rounded"
                      >
                        <Key className="h-3 w-3" />
                        ¿Olvidaste tu contraseña?
                      </Link>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Columna Derecha - Información de Ayuda */}
            <div className="flex flex-col justify-center space-y-3">
              
              {/* Sección de Beneficios */}
              <div className="space-y-3">
                <div className="text-center lg:text-left">
                  <h2 className="text-lg lg:text-xl font-bold text-foreground mb-1">
                    ¿Qué puedes hacer?
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    Descubre todas las funcionalidades disponibles
                  </p>
                </div>

                {/* Cards de Beneficios */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2">
                  <div className="p-2 bg-gradient-to-r from-primary/10 to-primary/5 rounded-md border border-primary/20 hover:shadow-sm transition-all duration-200">
                    <div className="flex items-start gap-1.5">
                      <div className="p-1 bg-primary/20 rounded-sm">
                        <Users className="h-3 w-3 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-0.5 text-xs">
                          Gestión Personalizada
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          Accede a información específica según tu rol en el
                          sistema
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2 bg-gradient-to-r from-green-500/10 to-green-500/5 rounded-md border border-green-500/20 hover:shadow-sm transition-all duration-200">
                    <div className="flex items-start gap-1.5">
                      <div className="p-1 bg-green-500/20 rounded-sm">
                        <CheckCircle2 className="h-3 w-3 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-0.5 text-xs">
                          Transacciones Seguras
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          Realiza compras y gestiones con total seguridad
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2 bg-gradient-to-r from-blue-500/10 to-blue-500/5 rounded-md border border-blue-500/20 hover:shadow-sm transition-all duration-200">
                    <div className="flex items-start gap-1.5">
                      <div className="p-1 bg-blue-500/20 rounded-sm">
                        <Clock className="h-3 w-3 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-0.5 text-xs">
                          Acceso 24/7
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          Gestiona tus productos en cualquier momento
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2 bg-gradient-to-r from-purple-500/10 to-purple-500/5 rounded-md border border-purple-500/20 hover:shadow-sm transition-all duration-200">
                    <div className="flex items-start gap-1.5">
                      <div className="p-1 bg-purple-500/20 rounded-sm">
                        <Zap className="h-3 w-3 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-0.5 text-xs">
                          Proceso Rápido
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          Interfaz intuitiva para una experiencia fluida
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sección de Credenciales de Prueba */}
              {/* <div className="p-3 bg-gradient-to-r from-muted/50 to-muted/30 rounded-md border border-border/50">
                <h3 className="font-semibold text-xs text-foreground mb-2 flex items-center gap-1">
                  <Shield className="h-2.5 w-2.5 text-primary" />
                  🔑 Credenciales de Prueba
                </h3>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 p-1 bg-background/50 rounded-sm">
                    <span className="font-medium">Admin:</span>
                    <span>RUT: 1-9, Contraseña: admin</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 p-1 bg-background/50 rounded-sm">
                    <span className="font-medium">Apoderado:</span>
                    <span>RUT: 2-7, Contraseña: apoderado</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 p-1 bg-background/50 rounded-sm">
                    <span className="font-medium">Cajero:</span>
                    <span>RUT: 3-5, Contraseña: cajero</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 p-1 bg-background/50 rounded-sm">
                    <span className="font-medium">Personal de Entrega:</span>
                    <span>RUT: 16.234.567-8, Contraseña: 123456</span>
                  </div>
                </div>
              </div> */}

              {/* Sección de Seguridad */}
              <div className="p-2 bg-gradient-to-r from-amber-500/10 to-amber-500/5 rounded-md border border-amber-500/20">
                <h4 className="font-medium text-xs mb-1 text-amber-600 flex items-center gap-1">
                  <Lock className="h-2.5 w-2.5" />
                  Seguridad Garantizada
                </h4>
                <ul className="text-xs text-amber-600/80 space-y-0.5">
                  <li>• Encriptación de datos de alta seguridad</li>
                  <li>• Protección contra ataques informáticos</li>
                  <li>• Cumplimiento de estándares de privacidad</li>
                  <li>• Acceso restringido por roles</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Botón de regreso al final */}
      <div className="p-4 sm:p-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 h-7 hover:bg-primary/10 transition-colors text-xs"
        >
          <ArrowLeft className="h-3 w-3" />
          <Store className="h-3 w-3" />
          Volver a la Tienda
        </Button>
      </div>
    </div>
  );
};

export default LoginPage;
